import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PageContentContextType {
  hasContent: boolean;
  isLoading: boolean;
  hasError: boolean;
  setPageState: (state: { hasContent?: boolean; isLoading?: boolean; hasError?: boolean }) => void;
  resetPageState: () => void;
}

const PageContentContext = createContext<PageContentContextType | undefined>(undefined);

export const usePageContent = () => {
  const context = useContext(PageContentContext);
  if (!context) {
    throw new Error('usePageContent must be used within a PageContentProvider');
  }
  return context;
};

interface PageContentProviderProps {
  children: ReactNode;
}

export const PageContentProvider: React.FC<PageContentProviderProps> = ({ children }) => {
  const [hasContent, setHasContent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const setPageState = useCallback((state: { hasContent?: boolean; isLoading?: boolean; hasError?: boolean }) => {
    if (state.hasContent !== undefined) setHasContent(state.hasContent);
    if (state.isLoading !== undefined) setIsLoading(state.isLoading);
    if (state.hasError !== undefined) setHasError(state.hasError);
  }, []);

  const resetPageState = useCallback(() => {
    setHasContent(false);
    setIsLoading(true);
    setHasError(false);
  }, []);

  const value = React.useMemo(() => ({
    hasContent,
    isLoading,
    hasError,
    setPageState,
    resetPageState,
  }), [hasContent, isLoading, hasError, setPageState, resetPageState]);

  return (
    <PageContentContext.Provider value={value}>
      {children}
    </PageContentContext.Provider>
  );
};

